import React from 'react';
import { array, bool, func, object, string } from 'prop-types';
import { contains } from 'ramda';

import { ConversationDetailsContext } from '../../context';
import { CONVERSATION_ERROR, GET_MESSAGES, REPLY_ERROR, REPLY_TO_CONVERSATION } from '../../constants/ActionTypes';
import Layout from '../Layout';
import Header from '../Header';
import MessagesList from '../MessagesList';
import MessageInput from '../MessageInput';
import SessionMessage from '../SessionMessage';
import ErrorMessage from '../ErrorMessage';
import Footer from '../Footer';

// Page styles
import cx from 'classnames';
import s from './Chat.css';


const Chat = ({ canReply, error, loadingState, messagesList, replyToConversationHandler, resetSessionTime, sessionTime, conversationHash }) => {

    // Tests will fail if we do not give this Ramda method what it expects, an array. this.props.loadingState.requests returns an array.
    const isLoadingAllMessages = contains(GET_MESSAGES, loadingState.requests || []);
    const isLoadingReply = contains(REPLY_TO_CONVERSATION, loadingState.requests || []);
    const errorMessageExists = error && error.data && error.data.error;

    const ua = navigator.userAgent.toLowerCase();
    const isAndroidWebView = ua.indexOf("android") > -1 && !window.chrome;

    function replyHandler(message, attachment) {
        replyToConversationHandler(message, attachment);
        resetSessionTime();
    }

    return (
        <Layout column fullscreen className={s.component}>
            <ConversationDetailsContext.Consumer>
                {conversationDetails => (
                    <Header
                        practiceName={conversationDetails ? conversationDetails.practiceName : 'Unknown'}
                        conversationHash={conversationHash}
                    />)}
            </ConversationDetailsContext.Consumer>

            {!error && isAndroidWebView &&
                <ErrorMessage
                    message={`Some older Android versions have issues downloading attachments. Open this conversation in Chrome to avoid this issue.`}
                />
            }

            {error && error.type === REPLY_ERROR && error.status === 413 &&
                <ErrorMessage
                    title="Sorry, an error occurred trying to load the conversation."
                    message={`Attachment is too large. Please restrict your attachment to 50MB or less.`}
                />
            }

            {error && error.type === REPLY_ERROR && error.status !== 413 &&
                <ConversationDetailsContext.Consumer>
                    {conversationDetails => (
                        <ErrorMessage
                            title="Sorry, an error occurred trying to reply to the conversation."
                            message={errorMessageExists ? error.data.error : `If this persists, please contact ${conversationDetails.practiceName}.`}
                        />
                    )}
                </ConversationDetailsContext.Consumer>
            }

            {error && error.type === CONVERSATION_ERROR &&
                <ConversationDetailsContext.Consumer>
                    {conversationDetails => (
                        <ErrorMessage
                            title="Sorry, an error occurred trying to load the conversation."
                            message={errorMessageExists ? error.data.error : `If this persists, please contact ${conversationDetails.practiceName}.`}
                        />
                    )}
                </ConversationDetailsContext.Consumer>
            }

            {/* canReply is part of the MessagesResponse - check if loading state is complete too, otherwise this flashes unexpectedly. */}
            {!canReply && !isLoadingAllMessages && !error &&
                <ConversationDetailsContext.Consumer>
                    {conversationDetails => (
                        <ErrorMessage
                            title={`This is a no-reply message from ${conversationDetails.practiceName}.`}
                            message={`For any questions or concerns, contact ${conversationDetails.practiceName}. For any urgent medical questions or emergencies contact 911.`}
                        />
                    )}
                </ConversationDetailsContext.Consumer>
            }

            {messagesList &&
                <MessagesList
                    error={error}
                    isLoadingAllMessages={isLoadingAllMessages}
                    messagesList={messagesList}
                />
            }

            <SessionMessage
                sessionTime={sessionTime}
            />

            {!error &&
                <div className={cx(!canReply ? s.fillerBottomSmall : s.fillerBottom)} />
            }

            {canReply &&
                <MessageInput
                    isLoadingReply={isLoadingReply}
                    replyHandler={replyHandler}
                />
            }

            <section className={s.footerContainer}>
                <Footer dark />
            </section>
        </Layout>
    );

};

Chat.propTypes = {
    canReply: bool,
    conversationHash: string,
    error: object,
    loadingState: object,
    messagesList: array,
    replyToConversationHandler: func.isRequired,
    resetSessionTime: func,
    sessionTime: object,
};

export default Chat;